/**
 * Hikki (2026-09-09): sound control for autoplaying product videos.
 * Videos autoplay muted (browser rule). A tap on the [data-sound-toggle] button next to a
 * video unmutes it; leaving the slide, scrolling the gallery away, or closing the lightbox
 * mutes again. Also used by the PhotoSwipe lightboxes, which inject the same button.
 */
const HikkiVideoSound = {
  toggleSelector: '[data-sound-toggle]',

  bind(root) {
    if (!root || root.dataset.soundBound === 'true') return;
    root.dataset.soundBound = 'true';
    root.addEventListener('click', event => {
      const button = event.target instanceof Element ? event.target.closest(this.toggleSelector) : null;
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      const video = button.parentElement && button.parentElement.querySelector('video');
      if (!video) return;
      this.set(video, button, video.muted);
    });
  },

  set(video, button, unmute) {
    video.muted = !unmute;
    button.classList.toggle('is-unmuted', unmute);
    button.setAttribute('aria-pressed', String(unmute));
    button.setAttribute('aria-label', unmute ? button.dataset.labelMute : button.dataset.labelUnmute);
    const text = button.querySelector('[data-sound-toggle-text]');
    if (text) text.textContent = unmute ? button.dataset.labelMute : button.dataset.labelUnmute;
    if (unmute && video.paused) video.play().catch(() => {});
    // Hikki (2026-09-11): the icon button has no visible text, so tell Clarity explicitly.
    if (typeof window.clarity === 'function') {
      try { window.clarity('event', unmute ? 'video_unmute' : 'video_mute'); } catch (e) {}
    }
  },

  muteAll(root) {
    if (!root) return;
    root.querySelectorAll('video').forEach(video => {
      if (video.muted) return;
      const button = video.parentElement && video.parentElement.querySelector(this.toggleSelector);
      if (button) this.set(video, button, false);
      else video.muted = true;
    });
  },

  // PhotoSwipe html slides: the injected <video autoplay> does not reliably start on its own,
  // so start it here and give it the same sound button.
  activateLightboxVideo(content) {
    const element = content && content.element;
    const video = element && element.querySelector ? element.querySelector('video') : null;
    if (!video) return;
    video.muted = true;
    video.play().catch(() => {});
    if (!element.querySelector(this.toggleSelector)) {
      const template = document.querySelector('[data-sound-toggle-template]');
      if (template && template.content && template.content.firstElementChild) {
        element.appendChild(template.content.firstElementChild.cloneNode(true));
      }
    }
    this.bind(element);
  },

  deactivateLightboxVideo(content) {
    const element = content && content.element;
    const video = element && element.querySelector ? element.querySelector('video') : null;
    if (!video) return;
    video.pause();
    this.muteAll(element);
  }
};

/**
 * Hikki (2026-09-16): inline gallery videos ship with no <source> (snippets/product-media-card.liquid
 * puts the MP4 URL in data-src: 480p for the mobile slider, 1080p for the desktop grid). Attach it
 * only once the tile is on screen and its poster has painted, so the poster stays the LCP element,
 * the download never races it, and a tile hidden by the breakpoint (mobile slider on desktop,
 * desktop grid on phones) never downloads at all.
 */
const HikkiDeferredVideo = {
  selector: 'video[data-deferred-video][data-src]',

  init(root) {
    const videos = (root || document).querySelectorAll(this.selector);
    if (!videos.length) return;
    if (!('IntersectionObserver' in window)) {
      videos.forEach(video => this.afterPosterPaint(video, () => this.attach(video)));
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        this.afterPosterPaint(entry.target, () => this.attach(entry.target));
      });
    }, { threshold: 0.01 });
    videos.forEach(video => {
      if (video.dataset.deferredObserved === 'true') return;
      video.dataset.deferredObserved = 'true';
      observer.observe(video);
    });
  },

  // Runs fn two frames after the poster image has loaded (or straight away if it already has),
  // with a 3 s ceiling so a slow or failed poster never keeps the video off. A hidden document
  // (background tab) gets no frames and no LCP measurement, so there fn runs right away.
  afterPosterPaint(video, fn) {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (document.visibilityState === 'hidden') return setTimeout(fn, 0);
      requestAnimationFrame(() => requestAnimationFrame(fn));
    };
    const poster = video.getAttribute('poster');
    if (!poster) return finish();
    const image = new Image();
    image.onload = finish;
    image.onerror = finish;
    image.src = poster;
    if (image.complete) finish();
    setTimeout(finish, 3000);
  },

  attach(video) {
    const src = video.dataset.src;
    if (!src) return;
    delete video.dataset.src;
    const fallback = video.dataset.srcFallback;
    if (fallback) {
      delete video.dataset.srcFallback;
      // The 480p URL is derived from the 1080p name; if it does not exist, use the 1080p file.
      video.addEventListener('error', () => {
        video.src = fallback;
        video.play().catch(() => {});
      }, { once: true });
    }
    video.src = src;
    video.play().catch(() => {});
  }
};

/**
 * Base Swiper Class
 * Optimization: Used DOMContentLoaded instead of 'load' event.
 */
class InstanceSwiper extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute('handle');
    this.swiperInitialized = false; // Prevent multiple initializations
  }

  static get observedAttributes() {
    return ['handle'];
  }

  init() {
    // Stop if already initialized or element is not visible
    if (this.swiperInitialized) return;

    // Skip initialization if element is not visible (has no dimensions)
    if (this.offsetWidth === 0 && this.offsetHeight === 0 && !this.checkVisibility()) return;

    this.swiperInitialized = true;

    this.options = this.setOptions();
    this.instance = new Swiper('.swiper--' + this.handle, this.options);
    this.setInteractions();
  }

  /**
   * Checks if the element is visible
   */
  checkVisibility() {
    return !!(this.offsetWidth || this.offsetHeight || this.getClientRects().length);
  }

  connectedCallback() {
    if (Shopify.designMode) {
      window.addEventListener('shopify:section:load', () => this.init());
      window.addEventListener('shopify:section:select', () => this.init());

      // Fallback for Design mode
      setTimeout(() => {
        if (!this.swiperInitialized) this.init();
      }, 100);
    } else {
      // OPTIMIZATION: Not waiting for window 'load'. Starts immediately if DOM is ready.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }
  }

  disconnectedCallback() {
    if (this.instance && this.instance.destroy) this.instance.destroy();
  }

  /**
   * Settings before initialization
   */
  setOptions() {
    this._options = this.getOptionsAsJsonScripts();
    return this._options;
  }

  getOptionsAsJsonScripts() {
    const jsonScriptAutomated = this.querySelector(`script#swiper--${this.handle}--automated-options`);
    const jsonScriptOverwrite = this.querySelector(`script#swiper--${this.handle}--overwrite-options`);
    let options = {};

    const parseJSON = scriptElement => {
      if (scriptElement && scriptElement.textContent) {
        try {
          return JSON.parse(scriptElement.textContent);
        } catch (error) {
          console.error('JSON Parse Error', error, scriptElement?.textContent);
          return {};
        }
      }
      return {};
    };

    options = { ...options, ...parseJSON(jsonScriptAutomated) };
    options = { ...options, ...parseJSON(jsonScriptOverwrite) };

    return options;
  }

  /** Thumbs instance should be mounted before this instance */
  getThumbsInstance() {
    if (!this.isThumbsActive) return;
    return document.querySelector(`[handle="${this.getAttribute('thumbs')}"]`)?.instance;
  }

  /**
   * Interactions after initialization
   */
  setInteractions() {
    // console.log('setInteractions');
  }
}

/**
 * Product Thumbnails Class
 * Optimization: Used ResizeObserver instead of setTimeout(2500).
 */
class InstanceSwiperProductThumbs extends InstanceSwiper {
  constructor() {
    super();
  }

  setOptions() {
    super.setOptions();
    this.setCenteredSlides();
    return this._options;
  }

  setCenteredSlides() {
    if (this.handle !== 'product-thumbs') return;

    const afterInit = swiper => {
      swiper.slideTo(swiper.activeIndex);
      const swiperWrapper = swiper.wrapperEl;

      const observer = new MutationObserver(resetSwiperWrapperTransform);
      observer.observe(swiperWrapper, { attributes: true });

      function resetSwiperWrapperTransform() {
        if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform !== 'translate3d(0px, 0px, 0px)'
        ) {
          swiperWrapper.style.transform = 'translate3d(0px, 0px, 0px)';
        } else if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
        ) {
          setTimeout(() => {
            if (
              swiperWrapper.hasAttribute('style') &&
              swiperWrapper.style?.transform &&
              swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
            ) {
              observer.disconnect();
            }
          }, 500);
        }
      }

      /**
       * OPTIMIZATION: Quick fix for thumbnail dislocation issue.
       * Used ResizeObserver instead of fixed 2500ms wait.
       */
      const slideElement = this.querySelector('.swiper-slide');

      const calculateAndSetHeight = () => {
        if (!slideElement) return;
        const slideHeight = slideElement.clientHeight;
        // Do not calculate if not rendered (is 0)
        if (!slideHeight) return;

        const calculateAllThumbHeight = (slideHeight + 16) * this.querySelectorAll('.swiper-slide').length;
        const thumbsWrapper = this.parentElement;

        if (thumbsWrapper.offsetHeight === calculateAllThumbHeight || calculateAllThumbHeight <= thumbsWrapper.offsetHeight) return;

        const calculatedHeight = calculateAllThumbHeight - 16 + 'px';

        if (thumbsWrapper.offsetHeight > calculateAllThumbHeight) {
          thumbsWrapper.style.height = calculatedHeight;
        } else {
          thumbsWrapper.removeAttribute('style');
        }
      };

      // Initial run
      calculateAndSetHeight();

      // Listen for changes (CSS loading, screen size change, etc.)
      const resizeObserver = new ResizeObserver(() => {
        calculateAndSetHeight();
      });

      // Observe parent element size changes
      if (this.parentElement) {
        resizeObserver.observe(this.parentElement);
      }
    };

    this._options = {
      ...this._options,
      on: {
        ...(this._options.on || {}),
        afterInit: afterInit
      }
    };
  }
}

if (!customElements.get('swiper-product-thumbs')) {
  customElements.define('swiper-product-thumbs', InstanceSwiperProductThumbs);
}

/**
 * Main Product Gallery Class
 */
class InstanceSwiperProductGallery extends InstanceSwiper {
  constructor() {
    super();
    this.isThumbsActive = this.hasAttribute('thumbs');
    this.isZoomActive = this.hasAttribute('zoom');
    this.modelViewerBtn = this.querySelector('.model-viewer-btn');
    this.modelViewer = this.querySelector('model-viewer');
    this.isModelActive = false;
  }

  static get observedAttributes() {
    return ['thumbs', 'zoom'];
  }

  connectedCallback() {
    super.connectedCallback();
    HikkiVideoSound.bind(this);
    HikkiDeferredVideo.init(this);
    this.muteWhenOffscreen();
  }

  /**
   * Hikki: an unmuted video must not keep playing sound once the gallery has scrolled away.
   */
  muteWhenOffscreen() {
    if (!('IntersectionObserver' in window) || this.offscreenObserver) return;
    this.offscreenObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) HikkiVideoSound.muteAll(this);
      });
    }, { threshold: 0.2 });
    this.offscreenObserver.observe(this);
  }

  setOptions() {
    super.setOptions();
    this.setThumbOptions();
    return this._options;
  }

  setThumbOptions() {
    if (!this.isThumbsActive) return;
    const thumbsInstance = super.getThumbsInstance();
    this._options = {
      ...this._options,
      thumbs: {
        swiper: thumbsInstance
      }
    };
  }

  /**
   * Hikki fix (2026-09-09): on mobile only the small 25px expand icon opened the
   * lightbox, so taps on the photo itself went dead (Clarity: thousands of dead
   * clicks on the product image). Forward a plain tap on an image slide to that
   * slide's PhotoSwipe opener. Swiper cancels the click after a drag
   * (preventClicks), so swipes are untouched.
   */
  bindTapToZoom() {
    if (this.dataset.tapZoomBound) return;
    this.dataset.tapZoomBound = 'true';

    this.addEventListener('click', event => {
      if (event.defaultPrevented) return; // Swiper cancelled it: this was a swipe
      if (this.dataset.zoomReady !== 'true') return; // lightbox not initialised, keep old behaviour
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('a, button, model-viewer, video, iframe, .model-viewer-btn, .swiper-pagination, .swiper-button-next, .swiper-button-prev')) return;

      const slide = target.closest('.swiper-slide');
      if (!slide || slide.dataset.mediaType !== 'image') return;

      const toggle = slide.querySelector('a.product__gallery-toggle');
      if (!toggle) return;

      event.preventDefault();
      toggle.click();
    });
  }

  /**
   * Interactions after initialization
   */
  setInteractions() {
    if (this.isThumbsActive) this.setThumbsInteraction();
    if (this.isZoomActive) this.photoSwipeLightboxInit();
    if (this.isZoomActive) this.bindTapToZoom();
    this.initModelViewer();

    // Add thumb click handler for model slides
    const thumbSlides = document.querySelectorAll('.swiper-slide[data-media-type="model"]');
    thumbSlides.forEach(slide => {
      slide.addEventListener('click', () => this.enableSwiper());
    });

    // Force pagination update on mobile after each slide change
    this.instance.on('slideChange', swiper => {
      HikkiVideoSound.muteAll(this); // Hikki: leaving a slide mutes its video again
      if (DeviceDetector.isMobile()) {
        swiper.pagination.update();
      }
    });

    // Detect scroll event and force update pagination when scrolling ends
    this.instance.on('scroll', swiper => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        swiper.pagination.update();
      }, 200);
    });
  }

  initModelViewer() {
    // Find all swiper slides with model viewers
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    this.resetAllModelViewers();
    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const container = slide.querySelector('.model-viewer-container');
      const modelViewer = container?.querySelector('model-viewer');

      if (btn && container && modelViewer) {
        // Store references to associated elements
        btn.modelViewer = modelViewer;
        modelViewer.controlButton = btn;

        // Model viewer container click handler
        container.addEventListener('click', e => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
            btn.click();
          }
        });

        // Model viewer button click handler
        btn.addEventListener('click', e => {
          e.preventDefault();
          this.toggleModelViewer(btn, modelViewer);
        });

        // Prevent model-viewer direct interaction
        modelViewer.addEventListener('mousedown', e => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
      }
    });

    // Add navigation click handlers
    if (this.instance?.navigation) {
      const { nextEl, prevEl } = this.instance.navigation;
      if (nextEl) {
        nextEl.addEventListener('click', () => {
          this.resetAllModelViewers();
          this.enableSwiper(true);
        });
      }
      if (prevEl) {
        prevEl.addEventListener('click', () => {
          this.resetAllModelViewers();
          this.enableSwiper(true);
        });
      }
    }
  }

  resetAllModelViewers() {
    // Find all model viewers in the current gallery instance
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const modelViewer = slide.querySelector('model-viewer');

      if (btn && modelViewer) {
        btn.classList.remove('is-active');
        modelViewer.interactionPrompt = 'none';
        modelViewer.cameraControls = false;
      }
    });

    this.isModelActive = false;
    this.instance && (this.instance.allowTouchMove = true);
  }

  toggleModelViewer(btn, modelViewer) {
    const wasActive = btn.classList.contains('is-active');

    // Reset all model viewers in this gallery
    this.resetAllModelViewers();

    if (!wasActive) {
      this.isModelActive = true;
      this.instance.allowTouchMove = false;

      // Enable only this specific model viewer
      modelViewer.interactionPrompt = 'auto';
      modelViewer.cameraControls = true;
      btn.classList.add('is-active');
    }
  }

  temporarilyDisableSwiper() {
    // Only disable touch/mouse dragging
    this.instance.allowTouchMove = false;

    // Re-enable after a short delay if no model interaction occurs
    this.swiperTimeout = setTimeout(() => {
      if (!this.isModelActive) {
        this.enableSwiper();
      }
    }, 100);
  }

  enableSwiper(force = false) {
    if (this.swiperTimeout) {
      clearTimeout(this.swiperTimeout);
    }

    // Always enable touch/mouse dragging if force is true or model is not active
    if (force || !this.isModelActive) {
      this.instance.allowTouchMove = true;

      // Reset model viewer state if forced
      if (force && this.isModelActive) {
        this.isModelActive = false;
        if (this.modelViewer) {
          this.modelViewer.interactionPrompt = 'none';
          this.modelViewer.cameraControls = false;
        }
        this.modelViewerBtn.classList.remove('is-active');
      }
    }
  }

  setThumbsInteraction() {
    if (!this.isThumbsActive || !this.instance) return;
    const thumbsSwiper = this.options.thumbs.swiper;
    
    if(!thumbsSwiper)return; // Hikki bugfix

    // Enable swiper on thumb click
    thumbsSwiper.el.addEventListener('click', () => {
      this.enableSwiper(true);
      if (this.modelViewerBtn) {
        this.modelViewerBtn.classList.remove('is-active');
      }
    });

    this.instance.on('slideChangeTransitionStart', function (swiper) {
      const activeIndex = swiper.activeIndex;
      const thumbsActiveIndex = thumbsSwiper.activeIndex;
      if (activeIndex !== thumbsActiveIndex) {
        thumbsSwiper.slideTo(activeIndex);
      }
    });
  }

  photoSwipeLightboxInit() {
    const [closeIcon, prevIcon, nextIcon] = [
      document.querySelector('[data-close-icon]'),
      document.querySelector('[data-prev-icon]'),
      document.querySelector('[data-next-icon]')
    ];

    const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
      gallery: `.photoswipe-wrapper`,
      children: 'a.product__gallery-toggle',
      mainClass: 'pswp--product-media-gallery',

      loop: false,
      showHideAnimationType: 'zoom',

      // Hikki fix (2026-09-09): mobile used to open at vFill, which blows a landscape
      // photo up ~3x on a portrait phone. That leaves no background to tap and
      // disables PhotoSwipe's swipe-down-to-close (only active at 'fit'), so people
      // got stuck in the lightbox. Open at 'fit' everywhere; double-tap / pinch
      // still zooms in (mobile uses PhotoSwipe's own default secondary level).
      initialZoomLevel: zoomLevelObject => zoomLevelObject.fit,
      secondaryZoomLevel: zoomLevelObject => {
        if (DeviceDetector.isMobile()) {
          return Math.min(1, zoomLevelObject.fit * 3);
        } else {
          return 1;
        }
      },
      // Hikki fix (2026-09-09): on touch devices PhotoSwipe runs `tapAction` for a tap
      // anywhere in the viewport, background included, and the default
      // 'toggle-controls' just hides the close button. Keep that behaviour on the
      // photo itself, but close the lightbox when the tap lands on the background.
      tapAction: function (point, originalEvent) {
        const target = originalEvent && originalEvent.target;
        const onMedia = target instanceof Element && target.closest('.pswp__zoom-wrap');
        if (onMedia) {
          if (this.element) this.element.classList.toggle('pswp--ui-visible');
        } else {
          this.close();
        }
      },
      pswpModule: PhotoSwipe
    });
    photoSwipeLightboxInstance.addFilter('uiElement', (element, data) => {
      if (data.name === 'close') {
        element.innerHTML = closeIcon.innerHTML;
      } else if (data.name === 'arrowPrev') {
        element.innerHTML = prevIcon.innerHTML;
      } else if (data.name === 'arrowNext') {
        element.innerHTML = nextIcon.innerHTML;
      }
      return element;
    });

    // html for video
    photoSwipeLightboxInstance.addFilter('itemData', (itemData, index) => {
      if (itemData.type === 'html' && itemData.element) {
        return {
          html: itemData.element.dataset.pswpHtml
        };
      }
      return itemData;
    });

    // Hikki (2026-09-09): start lightbox videos and give them the sound button; mute on leave/close.
    photoSwipeLightboxInstance.on('contentActivate', ({ content }) => HikkiVideoSound.activateLightboxVideo(content));
    photoSwipeLightboxInstance.on('contentDeactivate', ({ content }) => HikkiVideoSound.deactivateLightboxVideo(content));
    photoSwipeLightboxInstance.on('close', () => {
      const pswp = photoSwipeLightboxInstance.pswp;
      if (pswp && pswp.element) HikkiVideoSound.muteAll(pswp.element);
    });

    photoSwipeLightboxInstance.init();
    this.dataset.zoomReady = 'true'; // Hikki: tap-to-zoom only forwards taps once the lightbox exists

    photoSwipeLightboxInstance.on('beforeOpen', () => {
      document.body.classList.add('overflow-hidden');
      const videos = this.querySelectorAll('video');
      Array.from(videos).forEach(video => {
        // If video is not playing, call playPromise
        // Force to play
        video
          .play()
          .then(() => {
            // Automatic playback started!
            // Show playing UI.
          })
          .catch(error => {
            // Auto-play was prevented
            // Show paused UI.
            video.pause();
          });
      });
    });

    photoSwipeLightboxInstance.on('closingAnimationStart', () => {
      document.body.classList.remove('overflow-hidden');
    });
  }

  setActiveMedia(id) {
    const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(media => Number(media.dataset.mediaId) === id);

    if (!mediaFound) return;

    if (this.instance && typeof this.instance.slideTo === 'function') {
      if (this.dataset.hideOtherVariantsMedia === 'false') {
        this.instance.slideTo(Number(mediaFound.dataset.index));
      }
    }
  }
}
if (!customElements.get('swiper-product-gallery')) {
  customElements.define('swiper-product-gallery', InstanceSwiperProductGallery);
}

/**
 * Product Media Info Class
 */
class ProductMediaInfo extends HTMLElement {
  constructor() {
    super();

    this.init();
    window.addEventListener('resize', this.init.bind(this));

    if (Shopify.designMode) {
      window.addEventListener('shopify:section:load', this.init.bind(this));
    }
  }

  init() {
    let containerOffsetWidth = document.querySelector('.main-product__media--slider')?.offsetWidth || 330;
    if (document.querySelector('.main-product__media--grid-item') && !DeviceDetector.isMobile()) {
      const gridItem = document.querySelector('.main-product__media--grid-item');
      if (gridItem) containerOffsetWidth = gridItem.offsetWidth;
    }
    const maxWidthForInfo = (containerOffsetWidth - 48) / 2;

    const lastP = this.querySelector('p:last-child');
    if (!lastP) return;

    const mediaInfoTextWidth = lastP.offsetWidth;
    const mediaInfoHidden = this.querySelector('p[aria-hidden]');

    if (mediaInfoTextWidth > maxWidthForInfo) {
      this.classList.remove('animation-stopped');
      this.style.cssText = `--marquee-speed: ${(mediaInfoTextWidth / maxWidthForInfo) * 8}s`;
      if (mediaInfoHidden) mediaInfoHidden.style.display = '';
    } else {
      this.classList.add('animation-stopped');
      this.style.cssText = '';
      if (mediaInfoHidden) mediaInfoHidden.style.display = 'none';
    }
  }
}

if (!customElements.get('product-media-info')) {
  customElements.define('product-media-info', ProductMediaInfo);
}

/**
 * Hikki addition image gallery functionality grid view
 */
(function(){const initDeferredVideos=()=>HikkiDeferredVideo.init(document);document.readyState==='loading'?document.addEventListener('DOMContentLoaded',initDeferredVideos):initDeferredVideos()})();
(function(){function initGridPhotoSwipe(){const gridWrapper=document.querySelector(".main-product__media--grid.photoswipe-wrapper");if(!gridWrapper||!gridWrapper.querySelector("a.product__gallery-toggle"))return;if(typeof PhotoSwipeLightbox==="undefined"||typeof PhotoSwipe==="undefined")return;const[closeIcon,prevIcon,nextIcon]=[document.querySelector("[data-close-icon]"),document.querySelector("[data-prev-icon]"),document.querySelector("[data-next-icon]")],lightbox=new PhotoSwipeLightbox({gallery:".main-product__media--grid.photoswipe-wrapper",children:"a.product__gallery-toggle",mainClass:"pswp--product-media-gallery",loop:!1,showHideAnimationType:"zoom",initialZoomLevel:zoomLevelObject=>zoomLevelObject.fit,secondaryZoomLevel:1,pswpModule:PhotoSwipe});lightbox.addFilter("uiElement",(element,data)=>(data.name==="close"&&closeIcon?element.innerHTML=closeIcon.innerHTML:data.name==="arrowPrev"&&prevIcon?element.innerHTML=prevIcon.innerHTML:data.name==="arrowNext"&&nextIcon&&(element.innerHTML=nextIcon.innerHTML),element)),lightbox.addFilter("itemData",(itemData,index)=>itemData.type==="html"&&itemData.element?{html:itemData.element.dataset.pswpHtml}:itemData),lightbox.init(),lightbox.on("contentActivate",({content})=>HikkiVideoSound.activateLightboxVideo(content)),lightbox.on("contentDeactivate",({content})=>HikkiVideoSound.deactivateLightboxVideo(content)),lightbox.on("close",()=>{lightbox.pswp&&lightbox.pswp.element&&HikkiVideoSound.muteAll(lightbox.pswp.element)}),lightbox.on("beforeOpen",()=>{document.body.classList.add("overflow-hidden")}),lightbox.on("closingAnimationStart",()=>{document.body.classList.remove("overflow-hidden")})}document.readyState==="loading"?document.addEventListener("DOMContentLoaded",initGridPhotoSwipe):initGridPhotoSwipe()})();

